import React from "react";
import { Modal, Descriptions, Tag, Divider, Typography, Grid } from "antd";
import {
  FileTextOutlined,
  DollarOutlined,
  TruckOutlined,
  ToolOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import type { AccountingEntryResponse } from "@OrianaTypes";

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface AccountingDetailsModalProps {
  open: boolean;
  onClose: () => void;
  entry: AccountingEntryResponse | null;
}

const formatCurrency = (val?: string | null) => {
  if (!val) return "—";
  const num = parseFloat(val);
  if (isNaN(num)) return "—";
  return `${String.fromCharCode(0x20B9)} ${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val?: string | null) => {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 14px",
  borderRadius: "10px",
  marginBottom: "12px",
  marginTop: "6px",
  fontWeight: 600,
  fontSize: "13px",
  letterSpacing: "0.3px",
};

const noDuesTagColor: Record<string, string> = {
  yes: "green",
  no: "red",
  pending: "orange",
};

const AccountingDetailsModal: React.FC<AccountingDetailsModalProps> = ({
  open,
  onClose,
  entry,
}) => {
  const screens = useBreakpoint();
  const descColumn = screens.md ? 3 : 1;

  if (!entry) return null;

  return (
    <Modal
      title={
        <span style={{ fontWeight: 700, fontSize: "16px" }}>
          Accounting Entry #{entry.id}
        </span>
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
      destroyOnClose
      styles={{ body: { maxHeight: "80vh", overflowY: "auto", padding: "16px 24px" } }}
    >
      <div
        style={{
          ...sectionHeaderStyle,
          background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
          border: "1px solid #667eea30",
          color: "#4c51bf",
        }}
      >
        <FileTextOutlined />
        <span>Sales &amp; Invoice Master</span>
      </div>
      <Descriptions bordered size="small" column={descColumn}>
        <Descriptions.Item label="Tax Invoice No.">{entry.taxInvoiceNo || "—"}</Descriptions.Item>
        <Descriptions.Item label="Tax Invoice Date">{formatDate(entry.taxInvoiceDate)}</Descriptions.Item>
        <Descriptions.Item label="Invoice Amount">{formatCurrency(entry.invoiceAmount)}</Descriptions.Item>
        <Descriptions.Item label="Payment Mode">
          {entry.paymentMode ? <Tag color="blue">{entry.paymentMode.toUpperCase()}</Tag> : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Advance Amount">
          {entry.paymentType === "advance"
            ? formatCurrency(entry.paymentAmount)
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Advance Date">
          {entry.paymentType === "advance"
            ? formatDate(entry.paymentDate)
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Payment Ref No." span={descColumn}>{entry.multiplePaymentRefNo || "—"}</Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: "16px 0" }} />

      <div
        style={{
          ...sectionHeaderStyle,
          background: "linear-gradient(135deg, #11998e15 0%, #38ef7d15 100%)",
          border: "1px solid #11998e30",
          color: "#065f46",
        }}
      >
        <DollarOutlined />
        <span>Payment &amp; Collection Tracking</span>
      </div>
      <Descriptions bordered size="small" column={descColumn}>
        <Descriptions.Item label="Payment Date">{formatDate(entry.paymentDate)}</Descriptions.Item>
        <Descriptions.Item label="Due Date">{formatDate(entry.paymentDueDate)}</Descriptions.Item>
        <Descriptions.Item label="Payment Amount">{formatCurrency(entry.paymentAmount)}</Descriptions.Item>
        <Descriptions.Item label="TDS">{formatCurrency(entry.tds)}</Descriptions.Item>
        <Descriptions.Item label="Balance Amount">
          <Text strong style={{ color: "#22c55e" }}>{formatCurrency(entry.balanceAmount)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="No Due Clearance">
          {entry.noDuesClearanceStatus ? (
            <Tag color={noDuesTagColor[entry.noDuesClearanceStatus] || "default"}>
              {entry.noDuesClearanceStatus.charAt(0).toUpperCase() + entry.noDuesClearanceStatus.slice(1)}
            </Tag>
          ) : "—"}
        </Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: "16px 0" }} />

      <div
        style={{
          ...sectionHeaderStyle,
          background: "linear-gradient(135deg, #f85f7a15 0%, #fc466b15 100%)",
          border: "1px solid #f85f7a30",
          color: "#9b1c1c",
        }}
      >
        <TruckOutlined />
        <span>Dispatch Cost Tracking</span>
      </div>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Dispatch Expenses">{formatCurrency(entry.dispatchExpenses)}</Descriptions.Item>
        <Descriptions.Item label="Remark">{entry.dispatchExpensesRemark || "—"}</Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: "16px 0" }} />

      <div
        style={{
          ...sectionHeaderStyle,
          background: "linear-gradient(135deg, #f7971e15 0%, #ffd20015 100%)",
          border: "1px solid #f7971e30",
          color: "#92400e",
        }}
      >
        <ToolOutlined />
        <span>Commissioning Cost Tracking</span>
      </div>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Commissioning Expenses">{formatCurrency(entry.commissioningExpenses)}</Descriptions.Item>
        <Descriptions.Item label="Remark">{entry.commissioningExpensesRemark || "—"}</Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: "16px 0" }} />

      <div
        style={{
          ...sectionHeaderStyle,
          background: "linear-gradient(135deg, #a18cd115 0%, #fbc2eb15 100%)",
          border: "1px solid #a18cd130",
          color: "#5b21b6",
        }}
      >
        <CreditCardOutlined />
        <span>Credit Note &amp; Adjustment</span>
      </div>
      <Descriptions bordered size="small" column={descColumn}>
        <Descriptions.Item label="Credit Note Ref">{entry.creditNoteRef || "—"}</Descriptions.Item>
        <Descriptions.Item label="Credit Note Date">
          {entry.paymentType === "credit_note"
            ? formatDate(entry.paymentDate)
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Credit Note Amount">
          {entry.paymentType === "credit_note"
            ? formatCurrency(entry.paymentAmount)
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Remark" span={descColumn}>{entry.creditNoteRemark || "—"}</Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: "16px 0" }} />

      <Descriptions size="small" column={2} style={{ opacity: 0.65 }}>
        <Descriptions.Item label="Created By">{entry.createdBy || "—"}</Descriptions.Item>
        <Descriptions.Item label="Updated By">{entry.updatedBy || "—"}</Descriptions.Item>
        <Descriptions.Item label="Created At">{formatDate(entry.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Updated At">{formatDate(entry.updatedAt)}</Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

export default AccountingDetailsModal;
